import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FilthBackend } from "@/api/filth-backends-api"

import { Activity, BarChart3, Pencil, Plus, Server, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { getApiErrorMessage } from "@/lib/api-error-message"

import { AdminDashboardError } from "./admin-dashboard-error"
import { LoadingCard } from "./admin-dashboard-loading"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { FilthBackendConnectionDialog } from "./filth-backend-connection-dialog"
import {
  buildCreateFilthBackendInput,
  buildUpdateFilthBackendInput,
  EMPTY_FILTH_BACKEND_FORM,
  type FilthBackendFormState,
  formStateFromBackend,
  hasCredentialConflict,
} from "./filth-backends-form-utils"
import { FilthControlPlanePanel } from "./filth-control-plane-panel"
import { FilthInstanceSettingsDialog } from "./filth-instance-settings-dialog"
import { FilthSourcesPanel } from "./filth-sources-panel"
import {
  useCreateFilthBackendMutation,
  useDeleteFilthBackendMutation,
  useFilthBackendsQuery,
  useIntrospectFilthBackendMutation,
  useTestFilthBackendMutation,
  useUpdateFilthBackendMutation,
} from "../hooks/use-filth-backends"

function FilthContentBadge({ backend }: { backend: FilthBackend }) {
  const { t } = useTranslation()
  if (backend.consumer_mode === "primary") {
    return (
      <Badge variant="secondary">
        {t("adminDashboard.filthBackends.perUser", "Per-user spaces")}
      </Badge>
    )
  }
  if (backend.company_wide) {
    return (
      <Badge variant="secondary">
        {t("adminDashboard.filthBackends.companyWide", "Company-wide")}
      </Badge>
    )
  }
  return (
    <Badge variant="outline">
      {t("adminDashboard.filthBackends.unclassified", "Unclassified")}
    </Badge>
  )
}

export function FilthBackendsManagementCard() {
  const { t } = useTranslation()

  const { data: backends, isLoading, error, refetch } = useFilthBackendsQuery()
  const createMutation = useCreateFilthBackendMutation()
  const updateMutation = useUpdateFilthBackendMutation()
  const deleteMutation = useDeleteFilthBackendMutation()
  const testMutation = useTestFilthBackendMutation()
  const introspectMutation = useIntrospectFilthBackendMutation()

  const [settingsTarget, setSettingsTarget] = useState<FilthBackend | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<FilthBackend | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FilthBackend | null>(null)
  const [form, setForm] = useState<FilthBackendFormState>(EMPTY_FILTH_BACKEND_FORM)

  const openCreate = () => {
    setEditTarget(null)
    setForm(EMPTY_FILTH_BACKEND_FORM)
    setDialogOpen(true)
  }
  const openEdit = (backend: FilthBackend) => {
    setEditTarget(backend)
    setForm(formStateFromBackend(backend))
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    try {
      if (editTarget) {
        await updateMutation.mutateAsync({
          id: editTarget.id,
          input: buildUpdateFilthBackendInput(form),
        })
        toast.success(t("adminDashboard.filthBackends.toast.updated", "Prism backend updated"))
      } else {
        await createMutation.mutateAsync(buildCreateFilthBackendInput(form))
        toast.success(t("adminDashboard.filthBackends.toast.created", "Prism backend added"))
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(
        getApiErrorMessage(
          err,
          t("adminDashboard.filthBackends.toast.saveFailed", "Failed to save Prism backend")
        )
      )
    }
  }

  const handleToggle = async (backend: FilthBackend, enabled: boolean) => {
    try {
      await updateMutation.mutateAsync({ id: backend.id, input: { enabled } })
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to update status"))
    }
  }

  const handleTest = async (backend: FilthBackend) => {
    try {
      const res = await testMutation.mutateAsync(backend.id)
      if (res.success) {
        toast.success(`${backend.name}: healthy (${res.latency_ms ?? "?"} ms)`)
      } else {
        toast.error(`${backend.name}: ${res.error || "unreachable"}`)
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Test failed"))
    }
  }

  const handleIntrospect = async (backend: FilthBackend) => {
    try {
      const res = await introspectMutation.mutateAsync(backend.id)
      const s = res.stats
      if (res.success && s) {
        if (s.content_ready) {
          toast.success(`${backend.name}: ${s.file_count ?? 0} files indexed, content ready`)
        } else {
          toast.warning(
            `${backend.name}: ${s.file_count ?? 0} files, but content not ready — run a content crawl`
          )
        }
      } else {
        toast.error(`${backend.name}: ${res.error || "stats unavailable"}`)
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Introspect failed"))
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMutation.mutateAsync(deleteTarget.id)
      toast.success(t("adminDashboard.filthBackends.toast.deleted", "Prism backend removed"))
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Delete failed"))
    } finally {
      setDeleteTarget(null)
    }
  }

  const credentialConflict = hasCredentialConflict(form)
  const formValid =
    !credentialConflict &&
    form.name.trim() !== "" &&
    form.base_url.trim() !== "" &&
    (form.consumer_mode === "off" || form.delegated_scope.trim() !== "") &&
    (editTarget !== null || /^[a-z0-9][a-z0-9_-]{1,63}$/.test(form.id.trim().toLowerCase()))

  return (
    <CollapsibleManagementSection
      icon={<Server className="size-5" />}
      id="filth-data-sources"
      storageKey="admin-management-filth-backends-expanded"
      title={t("adminDashboard.filthBackends.title", "Prism data sources")}
      headerActions={
        <Button disabled={isLoading} size="sm" onClick={openCreate}>
          <Plus className="mr-2 size-4" />
          {t("adminDashboard.filthBackends.add", "Add Prism backend")}
        </Button>
      }
    >
      {isLoading && (
        <LoadingCard title={t("adminDashboard.filthBackends.title", "Prism data sources")} />
      )}
      {!isLoading && error && <AdminDashboardError error={error} onRetry={() => void refetch()} />}
      {!isLoading && !error && (
        <div className="space-y-8">
          <FilthControlPlanePanel backends={backends ?? []} />
          <Table className="[&_td]:px-4 [&_td]:py-4 [&_th]:px-4 [&_th]:py-4">
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminDashboard.filthBackends.col.name", "Name")}</TableHead>
                <TableHead>{t("adminDashboard.filthBackends.col.baseUrl", "Base URL")}</TableHead>
                <TableHead>
                  {t("adminDashboard.filthBackends.col.classification", "Content")}
                </TableHead>
                <TableHead>{t("adminDashboard.filthBackends.col.enabled", "Enabled")}</TableHead>
                <TableHead className="text-right">
                  {t("adminDashboard.filthBackends.col.actions", "Actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(backends ?? []).length === 0 && (
                <TableRow>
                  <TableCell className="text-muted-foreground text-center" colSpan={5}>
                    <div className="mx-auto flex max-w-prose flex-col items-center gap-4 py-12">
                      <Server
                        aria-hidden="true"
                        className="bg-muted size-12 rounded-lg border p-3"
                      />
                      <h5 className="font-display text-foreground">
                        {t(
                          "adminDashboard.filthBackends.empty",
                          "No Prism backends configured yet."
                        )}
                      </h5>
                      <p className="text-sm leading-relaxed">
                        {t("adminDashboard.filthSettings.emptyConnectionDescription")}
                      </p>
                      <Button onClick={openCreate}>
                        {t("adminDashboard.filthSettings.addConnection")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {(backends ?? []).map((backend) => (
                <Fragment key={backend.id}>
                  <TableRow>
                    <TableCell className="max-w-48 font-medium break-words whitespace-normal">
                      {backend.name}
                    </TableCell>
                    <TableCell className="max-w-64 font-mono text-xs break-all whitespace-normal">
                      {backend.base_url}
                    </TableCell>
                    <TableCell>
                      <FilthContentBadge backend={backend} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={backend.enabled}
                        onCheckedChange={(checked) => void handleToggle(backend, checked)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex min-w-40 flex-wrap justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSettingsTarget(backend)}
                        >
                          {t("adminDashboard.filthSettings.searchEngine")}
                        </Button>
                        <Button
                          size="icon"
                          title="Test /health"
                          variant="ghost"
                          onClick={() => void handleTest(backend)}
                        >
                          <Activity className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          title="Index stats"
                          variant="ghost"
                          onClick={() => void handleIntrospect(backend)}
                        >
                          <BarChart3 className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          title="Edit"
                          variant="ghost"
                          onClick={() => openEdit(backend)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          title="Delete"
                          variant="ghost"
                          onClick={() => setDeleteTarget(backend)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5}>
                      <FilthSourcesPanel backendId={backend.id} />
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <FilthInstanceSettingsDialog
        backend={settingsTarget}
        onClose={() => setSettingsTarget(null)}
      />
      <FilthBackendConnectionDialog
        busy={createMutation.isPending || updateMutation.isPending}
        credentialConflict={credentialConflict}
        dialogOpen={dialogOpen}
        editTarget={editTarget}
        form={form}
        formValid={formValid}
        handleSubmit={handleSubmit}
        setDialogOpen={setDialogOpen}
        setForm={setForm}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("adminDashboard.filthBackends.deleteDialog.title", "Delete Prism backend?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "adminDashboard.filthBackends.deleteDialog.description",
                "This removes the backend config and soft-deletes its Key Vault token. Libraries bound to it will stop returning results."
              )}
              {deleteTarget && (
                <span className="mt-2 block font-mono text-sm">{deleteTarget.name}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>
              {t("adminDashboard.filthBackends.deleteDialog.confirm", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CollapsibleManagementSection>
  )
}
